import { useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeftRight, ChevronDown, X, Maximize2 } from 'lucide-react'
import type { InfographicVersion } from '../../stores/infographicStore'

interface InfographicDiffViewProps {
  versions: InfographicVersion[]
  onClose: () => void
}

export default function InfographicDiffView({ versions, onClose }: InfographicDiffViewProps) {
  const [leftIndex, setLeftIndex] = useState(0)
  const [rightIndex, setRightIndex] = useState(Math.min(1, versions.length - 1))
  const [leftDropdownOpen, setLeftDropdownOpen] = useState(false)
  const [rightDropdownOpen, setRightDropdownOpen] = useState(false)
  const [lightboxImage, setLightboxImage] = useState<{ src: string; title: string } | null>(null)

  if (versions.length < 2) {
    return (
      <div className="text-center py-12">
        <p className="text-[var(--bbs-text-secondary)]">
          You need at least 2 versions to compare. Generate another version first!
        </p>
        <button type="button" onClick={onClose} className="btn btn-secondary mt-4">
          Close
        </button>
      </div>
    )
  }

  const leftVersion = versions[leftIndex]
  const rightVersion = versions[rightIndex]

  const handleSwap = () => {
    const temp = leftIndex
    setLeftIndex(rightIndex)
    setRightIndex(temp)
  }

  const formatTimestamp = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const getStyleLabel = (style?: string) => {
    if (!style) return 'Original'
    return style
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const renderInfographic = (version: InfographicVersion, side: 'left' | 'right') => {
    const infographic = version.infographic
    const isPink = side === 'left'
    const accentColor = isPink ? 'var(--bbs-color-primary)' : 'var(--bbs-color-accent)'
    const borderColor = isPink ? 'rgba(255, 0, 128, 0.3)' : 'rgba(4, 255, 178, 0.3)'
    const bgAccent = isPink ? 'bg-pink-50' : 'bg-[var(--bbs-color-accent)]/10'
    const borderAccent = isPink ? 'border-pink-300' : 'border-[var(--bbs-color-accent)]/30'

    // If it's a generated image
    if (infographic.imageBase64 && infographic.mimeType) {
      const imgSrc = `data:${infographic.mimeType};base64,${infographic.imageBase64}`
      return (
        <div
          className="relative group cursor-pointer"
          onClick={() => setLightboxImage({ src: imgSrc, title: infographic.title || 'Infographic' })}
        >
          <img
            src={imgSrc}
            alt={infographic.title || 'Infographic'}
            className="w-full h-auto rounded-xl transition-transform group-hover:scale-[1.02]"
            style={{ boxShadow: `0 0 20px ${borderColor}` }}
          />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-xl backdrop-blur-sm">
            <div className="bg-white/90 rounded-full p-3 shadow-xl">
              <Maximize2 className="w-6 h-6" style={{ color: accentColor }} />
            </div>
          </div>
        </div>
      )
    }

    // Otherwise render concept card
    return (
      <div
        className="p-6 rounded-xl bg-[var(--bbs-glass-card)] backdrop-blur-xl border space-y-4"
        style={{
          borderColor,
          boxShadow: `0 0 15px ${borderColor}`
        }}
      >
        <h3 className="text-xl font-bold text-center" style={{ color: accentColor }}>
          {infographic.title || 'Support Information'}
        </h3>

        {infographic.keyVisuals && infographic.keyVisuals.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: accentColor }}>
              Key Visuals
            </h4>
            <ul className="space-y-1.5 pl-2">
              {infographic.keyVisuals.map((visual, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-[var(--bbs-text-secondary)]">
                  <span
                    className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: accentColor }}
                  />
                  <span>{visual}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {infographic.primaryTakeaway && (
          <div className={`p-3 rounded-lg ${bgAccent} border ${borderAccent}`}>
            <p className="text-sm text-[var(--bbs-text-primary)]">
              {infographic.primaryTakeaway}
            </p>
          </div>
        )}

        {infographic.cta && (
          <p className="text-xs text-[var(--bbs-text-secondary)] text-center italic">
            {infographic.cta}
          </p>
        )}
      </div>
    )
  }

  const renderVersionSelector = (
    currentIndex: number,
    setIndex: (index: number) => void,
    isOpen: boolean,
    setIsOpen: (open: boolean) => void,
    side: 'left' | 'right'
  ) => {
    const isPink = side === 'left'
    const accentColor = isPink ? 'var(--bbs-color-primary)' : 'var(--bbs-color-accent)'

    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="btn btn-glass w-full justify-between gap-2"
          style={{ borderColor: isOpen ? accentColor : undefined }}
        >
          <div className="text-left flex-1">
            <div className="text-sm font-medium" style={{ color: accentColor }}>
              Version {versions.length - currentIndex}
            </div>
            <div className="text-xs text-[var(--bbs-text-tertiary)]">
              {formatTimestamp(versions[currentIndex].timestamp)}
              {versions[currentIndex].style && ` • ${getStyleLabel(versions[currentIndex].style)}`}
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-[var(--bbs-glass-card)] backdrop-blur-xl border border-[var(--bbs-color-border)] rounded-xl shadow-xl z-10 max-h-64 overflow-y-auto">
            {versions.map((version, index) => (
              <button
                key={version.id}
                type="button"
                onClick={() => {
                  setIndex(index)
                  setIsOpen(false)
                }}
                className={`w-full px-4 py-3 text-left hover:bg-[var(--bbs-color-accent)]/10 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                  index === currentIndex ? 'bg-[var(--bbs-color-accent)]/5' : ''
                }`}
              >
                <div className="text-sm font-medium text-[var(--bbs-text-primary)]">
                  Version {versions.length - index}
                  {index === currentIndex && (
                    <span className="ml-2 text-xs" style={{ color: accentColor }}>
                      (Selected)
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--bbs-text-tertiary)]">
                  {formatTimestamp(version.timestamp)}
                  {version.style && ` • ${getStyleLabel(version.style)}`}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-[var(--bbs-text-primary)]">Compare Versions</h2>
            <p className="text-sm text-[var(--bbs-text-secondary)] mt-1">
              Pink = Left • Jadeite = Right • Click images to inspect
            </p>
          </div>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>

      {/* Version Selectors */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4">
        <div>
          {renderVersionSelector(leftIndex, setLeftIndex, leftDropdownOpen, setLeftDropdownOpen, 'left')}
        </div>

        <button
          type="button"
          onClick={handleSwap}
          className="btn btn-glass self-start md:self-center"
          title="Swap versions"
        >
          <ArrowLeftRight className="w-4 h-4" />
        </button>

        <div>
          {renderVersionSelector(rightIndex, setRightIndex, rightDropdownOpen, setRightDropdownOpen, 'right')}
        </div>
      </div>

      {/* Side-by-side comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left (Pink) */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[var(--bbs-color-primary)]" />
            <span className="text-sm font-medium text-[var(--bbs-text-secondary)]">
              Version {versions.length - leftIndex}
            </span>
          </div>
          {renderInfographic(leftVersion, 'left')}
        </div>

        {/* Right (Jadeite) */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[var(--bbs-color-accent)]" />
            <span className="text-sm font-medium text-[var(--bbs-text-secondary)]">
              Version {versions.length - rightIndex}
            </span>
          </div>
          {renderInfographic(rightVersion, 'right')}
        </div>
      </div>
    </div>

    {/* Lightbox Modal */}
    {lightboxImage && createPortal(
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
        onClick={() => setLightboxImage(null)}
      >
        <button
          type="button"
          onClick={() => setLightboxImage(null)}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Close lightbox"
        >
          <X className="w-6 h-6 text-white" />
        </button>
        <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
          <img
            src={lightboxImage.src}
            alt={lightboxImage.title}
            className="w-full h-auto rounded-xl shadow-2xl"
          />
        </div>
      </div>,
      document.body
    )}
  </>
  )
}
